import type { LoginGiftRewardTransaction, LoginGiftStatus } from './service';

export type LoginGiftRewardNoticeKind = 'login' | 'register';

export type LoginGiftRewardNoticeCandidate = {
  kind: LoginGiftRewardNoticeKind;
  transactionId: string;
  transactionCreatedAt: string;
  credits: number;
  nextResetAt: string | null;
};

export const LOGIN_GIFT_RECENT_GRANT_WINDOW_MS = 10 * 60 * 1000;
export const LOGIN_GIFT_NOTICE_DURATION_MS = 4000;

function isRecentGrant(params: {
  tx: LoginGiftRewardTransaction;
  now: Date;
  recentGrantWindowMs?: number;
}) {
  const createdAtMs = Date.parse(params.tx.createdAt);
  if (!Number.isFinite(createdAtMs)) {
    return false;
  }

  const recentGrantWindowMs =
    params.recentGrantWindowMs ?? LOGIN_GIFT_RECENT_GRANT_WINDOW_MS;
  return createdAtMs >= params.now.getTime() - recentGrantWindowMs;
}

function buildRewardNoticeCandidate(params: {
  kind: LoginGiftRewardNoticeKind;
  tx: LoginGiftRewardTransaction | null;
  now: Date;
  nextResetAt: string;
  recentGrantWindowMs?: number;
}): LoginGiftRewardNoticeCandidate | null {
  if (
    !params.tx ||
    params.tx.amount <= 0 ||
    !isRecentGrant({
      tx: params.tx,
      now: params.now,
      recentGrantWindowMs: params.recentGrantWindowMs,
    })
  ) {
    return null;
  }

  return {
    kind: params.kind,
    transactionId: params.tx.id,
    transactionCreatedAt: params.tx.createdAt,
    credits: params.tx.amount,
    nextResetAt: params.kind === 'login' ? params.nextResetAt : null,
  };
}

export function buildLoginGiftRewardNoticeCandidates(params: {
  status: Pick<
    LoginGiftStatus,
    'latestDailyGiftTx' | 'latestRegisterGiftTx' | 'nextResetAt'
  >;
  now?: Date;
  recentGrantWindowMs?: number;
}) {
  const now = params.now ?? new Date();
  const notices = [
    buildRewardNoticeCandidate({
      kind: 'login',
      tx: params.status.latestDailyGiftTx,
      now,
      nextResetAt: params.status.nextResetAt,
      recentGrantWindowMs: params.recentGrantWindowMs,
    }),
    buildRewardNoticeCandidate({
      kind: 'register',
      tx: params.status.latestRegisterGiftTx,
      now,
      nextResetAt: params.status.nextResetAt,
      recentGrantWindowMs: params.recentGrantWindowMs,
    }),
  ];

  return notices.filter((notice): notice is LoginGiftRewardNoticeCandidate =>
    Boolean(notice)
  );
}
